import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Spin, Typography, Modal, message, Table, Card,
} from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { getOrder, deleteOrder, listOrderLineItems, type Order, type OrderLineItem } from '@/api/orderApi';
import { getCustomer } from '@/api/customerApi';
import { getContact } from '@/api/contactApi';
import { getQuote } from '@/api/quoteApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  confirmed: 'processing',
  processing: 'blue',
  shipped: 'cyan',
  delivered: 'success',
  cancelled: 'error',
};

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [order, setOrder] = useState<Order | null>(null);
  const [lineItems, setLineItems] = useState<OrderLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});

  const canWrite = hasPermission('orders', 'write');
  const canDelete = hasPermission('orders', 'delete');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [o, items] = await Promise.allSettled([
        getOrder(id),
        listOrderLineItems(id),
      ]);

      if (o.status === 'fulfilled') {
        setOrder(o.value);

        // Resolve entity names
        const names: Record<string, string> = {};
        const resolvers: Promise<void>[] = [];
        if (o.value.customer_id) {
          resolvers.push(
            getCustomer(o.value.customer_id)
              .then((c) => { names.customer = c.name ?? o.value.customer_id; })
              .catch(() => { names.customer = o.value.customer_id; })
          );
        }
        if (o.value.contact_id) {
          resolvers.push(
            getContact(o.value.contact_id)
              .then((c) => { names.contact = c.name ?? o.value.contact_id; })
              .catch(() => { names.contact = o.value.contact_id; })
          );
        }
        if (o.value.quote_id) {
          resolvers.push(
            getQuote(o.value.quote_id)
              .then((q) => { names.quote = q.title ?? o.value.quote_id; })
              .catch(() => { names.quote = o.value.quote_id; })
          );
        }
        await Promise.allSettled(resolvers);
        setResolvedNames(names);
      } else {
        setError(o.reason?.message || t('app.not_found'));
      }

      if (items.status === 'fulfilled') {
        setLineItems(items.value);
      }
    } catch {
      setError(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('orders.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteOrder(id);
          message.success(t('orders.delete_success'));
          navigate('/orders');
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  if (loading) return <Spin size="large" />;
  if (error || !order) {
    return (
      <div>
        <Typography.Text type="danger">{error || t('app.not_found')}</Typography.Text>
        <br />
        <Button onClick={() => navigate('/orders')}>{t('app.back')}</Button>
      </div>
    );
  }

  const lineItemColumns: ColumnsType<OrderLineItem> = [
    {
      title: '#',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60,
    },
    {
      title: t('orders.line_item.product_name'),
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true,
    },
    {
      title: t('orders.line_item.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '—',
    },
    {
      title: t('orders.line_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
    },
    {
      title: t('orders.line_item.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (val: number) => val?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
    {
      title: t('orders.line_item.discount'),
      dataIndex: 'discount_percentage',
      key: 'discount_percentage',
      width: 100,
      align: 'right',
      render: (val: number) => val > 0 ? `${val}%` : '—',
    },
    {
      title: t('orders.line_item.line_total'),
      dataIndex: 'line_total',
      key: 'line_total',
      width: 130,
      align: 'right',
      render: (val: number) => val?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/orders')}>
            {t('app.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>{order.title}</Title>
        </Space>
        <Space>
          {canWrite && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/orders/${id}/edit`)}
            >
              {t('app.edit')}
            </Button>
          )}
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('app.delete')}
            </Button>
          )}
        </Space>
      </div>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={t('orders.field.order_number')}>
          {order.order_number || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.status')}>
          <Tag color={STATUS_COLORS[order.status] ?? 'default'}>
            {t(`orders.status_options.${order.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.title')} span={2}>
          {order.title}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.description')} span={2}>
          {order.description || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.customer_id')}>
          {resolvedNames.customer || order.customer_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.contact_id')}>
          {order.contact_id ? (resolvedNames.contact || order.contact_id) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.quote_id')}>
          {order.quote_id ? (resolvedNames.quote || order.quote_id) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.order_date')}>
          {order.order_date ? new Date(order.order_date).toLocaleDateString() : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.currency')}>
          {order.currency}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.shipping_address')}>
          {order.shipping_address || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.payment_terms')}>
          {order.payment_terms || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.notes')} span={2}>
          {order.notes || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.tags')} span={2}>
          {(order.tags ?? []).length > 0
            ? order.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Card
        title={t('orders.line_items_title')}
        style={{ marginTop: 16 }}
        size="small"
      >
        <Table<OrderLineItem>
          dataSource={lineItems}
          columns={lineItemColumns}
          rowKey="id"
          pagination={false}
          size="small"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6} align="right">
                  <strong>{t('orders.field.subtotal')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>
                    {order.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {(order.discount_percentage > 0 || order.discount_amount > 0) && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    {t('orders.field.discount')}
                    {order.discount_percentage > 0 ? ` (${order.discount_percentage}%)` : ''}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    -{(order.discount_amount + order.subtotal * order.discount_percentage / 100)
                      .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              {order.tax_percentage > 0 && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    {t('orders.field.tax')} ({order.tax_percentage}%)
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    {order.tax_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6} align="right">
                  <strong>{t('orders.field.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>
                    {order.currency}{' '}
                    {order.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>

      <Descriptions bordered column={2} size="small" style={{ marginTop: 16 }}>
        <Descriptions.Item label={t('orders.field.created_by')}>
          {order.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.created_at')}>
          {new Date(order.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.updated_by')}>
          {order.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.updated_at')}>
          {new Date(order.updated_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('orders.field.branch_id')}>
          {order.branch_id}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
